import { Dispatch } from 'redux';
import commonHandler from '../errorHandlers/common-handler';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';
import VoyagerController from '../controllers/VoyagerController';
import {
  CreateVoyagerContactsParams,
  VoyagerAttributes,
  VoyagerContacts,
  VoyagerKeyType,
  VoyagerLeadsSearchQuery,
} from '../interfaces/voyagerLeads';
import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';

class VoyagerCRUDActions extends CrudActionContainer {
  retrieveLeadsBySearchAttributes: string;
  resetLeadsAction: string;
  busyAction: string;
  setFreezedCreditsCount: string;

  constructor() {
    super('VOYAGER');
    this.retrieveLeadsBySearchAttributes = super.makeActionKey(
      'RETRIEVED_LEADS_BY_SEACH_ATTRIBUTES',
    );
    this.resetLeadsAction = this.makeActionKey('RESET_SEARCHED_LEADS');
    this.busyAction = this.makeActionKey('ENTITY_BUSY');
    this.setFreezedCreditsCount = this.makeActionKey(
      'SET_FREEZED_CREDITS_COUNT',
    );
  }

  retrieveAttributes(args?: { isExtended?: boolean; withPrefix?: boolean }) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response: VoyagerAttributes[] =
          await VoyagerController.getAllAttributes(args);
        dispatch(this.retrievedAll(response));
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  retrieveRemoteAttributes(attribute: VoyagerKeyType, query: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await VoyagerController.retrieveRemoteAttributes(
          attribute,
          query,
        );
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  retrieveLocationAttributes(query: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();
        const fbPageId = activeFacebookPageIdSelector(state);

        const response = await VoyagerController.retrieveLocationAttributes(
          query,
          fbPageId,
        );
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  searchLeads(query: VoyagerLeadsSearchQuery) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      dispatch(this.busy(this.retrieveAllAction));
      const state = getState();
      const fbPageId = activeFacebookPageIdSelector(state);
      try {
        const response: VoyagerContacts = await VoyagerController.searchLeads(
          query,
          fbPageId,
        );
        dispatch({
          type: this.retrieveLeadsBySearchAttributes,
          contacts: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  createContacts(params: CreateVoyagerContactsParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await VoyagerController.createContacts(params);
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  resetLeads() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({
        type: this.resetLeadsAction,
      });
    };
  }
}

const voyagerCRUDActions = new VoyagerCRUDActions();

export default voyagerCRUDActions;

export const useVoyagerCRUDActions = getActionHookBuilder(voyagerCRUDActions, [
  'retrieveAttributes',
  'retrieveRemoteAttributes',
  'retrieveLocationAttributes',
  'searchLeads',
  'createContacts',
  'resetLeads',
]);
